import { Form, Formik } from "formik";
import { Check, Lock, Plus, Trash, Unlock, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useState } from "react";
import * as Yup from "yup";
import SideDrawer from "@/components/ui/SideDrawer";
import InputField from "@/components/form/InputField";
import DataGrid from "@/components/ui/DataGrid";
import DropdownMenu from "@/components/ui/DropdownMenu";
import { roleAndPermissionService } from "@/services/breeze/RoleAndPermissionService";
import { CreateUpdateRole, Role } from "@/types/breeze/RoleAndPermission";
import { PaginatedResult } from "@/types/breeze/Any";
import { useRoles } from "@/hooks/useCommonQueries";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { SkeletonPage } from "@/components/ui/Skeleton";
import CheckboxField from "@/components/form/CheckboxField";
import PageTemplate from "@/components/page-template/PageTemplate";
import UserManagementTabs from "@/components/users/UserManagementTabs";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";

const RolesAndPermissionsPage = withTenantAdminRequired(
  function RolesAndPermissionsPage() {
    const [isRolesDrawerOpen, setIsRolesDrawerOpen] = useState(false);
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const { currentTenant } = useAuth();

    const {
      data: roles,
      isLoading: isRolesLoading,
      error: rolesError,
    } = useRoles();

    const createRole = useMutation({
      mutationFn: (role: CreateUpdateRole) =>
        roleAndPermissionService.createRole(role),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["roles"] });
        setIsRolesDrawerOpen(false);
      },
    });

    const deleteRole = useMutation({
      mutationFn: (roleId: number) =>
        roleAndPermissionService.deleteRole(roleId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["roles"] });
      },
    });

    const rolesPaginatedData: PaginatedResult<Role> = useMemo(() => {
      const items =
        roles
          ?.filter((role) => !role.is_administrator)
          .sort((a, b) => a.name.localeCompare(b.name)) ?? [];
      return {
        items,
        page: 1,
        page_size: items.length,
        total_items: items.length,
        total_pages: 1,
        has_next_page: false,
        has_previous_page: false,
      };
    }, [roles]);

    if (isRolesLoading) {
      return <SkeletonPage title="User Management" />;
    }

    if (rolesError) {
      return <div>Error loading roles</div>;
    }

    return (
      <PageTemplate
        title="User Management"
        description="Accounts, the roles that grant them access, and the groups they belong to"
      >
        <UserManagementTabs active="roles" />
        <Flex justify="end" align="center" mb="4">
          <Button
            onClick={() => setIsRolesDrawerOpen(true)}
            variant="outline"
            size="1"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </Flex>

        <DataGrid
          data={rolesPaginatedData}
          columns={[
            {
              field: "name",
              header: "Name",
              flex: 1.2,
              minWidth: 160,
              cellRenderer: (role: Role) => (
                <Link
                  to={`/org/${currentTenant?.id}/settings/roles-and-permissions/role/${role.id}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {role.name}
                </Link>
              ),
            },
            {
              field: "description",
              header: "Description",
              flex: 2,
              minWidth: 200,
            },
            {
              field: "is_active",
              header: "Is Active",
              flex: 0.7,
              minWidth: 110,
              cellRenderer: (role: Role) => <BooleanCell value={role.is_active} />,
            },
            {
              field: "anyapi_access",
              header: "API Access",
              flex: 0.7,
              minWidth: 120,
              cellRenderer: (role: Role) => (
                <BooleanCell value={role.anyapi_access} />
              ),
            },
            {
              field: "locked",
              header: "Locked",
              flex: 0.6,
              minWidth: 100,
              cellRenderer: (role: Role) =>
                role.locked ? (
                  <Lock className="w-4 h-4 text-red-600" />
                ) : (
                  <Unlock className="w-4 h-4 text-green-600" />
                ),
            },
            {
              field: "created_at",
              header: "Created At",
              flex: 0.9,
              minWidth: 130,
              cellRenderer: (role: Role) => (
                <Text>{new Date(role.created_at).toLocaleDateString()}</Text>
              ),
            },
            {
              field: "updated_at",
              header: "Updated At",
              flex: 0.9,
              minWidth: 130,
              cellRenderer: (role: Role) => (
                <Text>{new Date(role.updated_at).toLocaleDateString()}</Text>
              ),
            },
          ]}
          actions={(role: Role) => (
            <DropdownMenu
              items={[
                {
                  label: "Delete",
                  icon: <Trash className="w-4 h-4" />,
                  onClick: async () => {
                    const confirmed = await confirm({
                      title: "Delete Role",
                      message: `Are you sure you want to delete the role "${role.name}"?`,
                      confirmText: "Delete",
                      cancelText: "Cancel",
                      variant: "danger",
                    });

                    if (confirmed) {
                      deleteRole.mutate(role.id);
                    }
                  },
                  className: "text-red-600",
                  disabled: role.locked,
                },
              ]}
            />
          )}
          storageKey="roles_and_permissions"
          isLoading={isRolesLoading}
          currentPage={1}
          currentPageSize={rolesPaginatedData.items.length}
          rowSelection="none"
          height="auto"
          showPagination={false}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
        />

        <SideDrawer
          isOpen={isRolesDrawerOpen}
          onClose={() => setIsRolesDrawerOpen(false)}
          title="Create New Role"
        >
          <div className="p-4">
            <Formik
              initialValues={{
                name: "",
                description: "",
                frontend_url: "",
                is_active: true,
                anyapi_access: false,
                permission_ids: [],
              }}
              onSubmit={(values) => {
                createRole.mutate(values);
              }}
              validationSchema={Yup.object({
                name: Yup.string()
                  .required("This field is required.")
                  .matches(
                    /^[a-zA-Z0-9\s]+$/,
                    "Must contain only letters, numbers and spaces",
                  ),
                description: Yup.string(),
                frontend_url: Yup.string(),
                is_active: Yup.boolean(),
                anyapi_access: Yup.boolean(),
              })}
            >
              <Form className="space-y-4">
                <InputField name="name" label="Role Name" required />
                <InputField name="description" label="Description" />
                <InputField
                  name="frontend_url"
                  label="External Application URL"
                  description="The URL of your external application that users with this role should be redirected to."
                  options={{
                    placeholder: "Enter application URL",
                  }}
                />
                <CheckboxField name="is_active" label="Is Active" />
                <CheckboxField
                  name="anyapi_access"
                  description="If enabled, this role will have access to the API."
                  label="API Access"
                />
                <div className="pt-4">
                  <Button type="submit" loading={createRole.isPending}>
                    Create Role
                  </Button>
                </div>
              </Form>
            </Formik>
          </div>
        </SideDrawer>
      </PageTemplate>
    );
  },
);

export default RolesAndPermissionsPage;

function BooleanCell({ value }: { value: boolean }) {
  return value ? (
    <Check className="w-4 h-4 text-green-600" aria-label="Yes" />
  ) : (
    <X className="w-4 h-4 text-red-600" aria-label="No" />
  );
}
